import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

export default function PartnerSignup() {
    const { partnerSignup, user, error, setError } = useAuth();
    const [name, setName] = useState("");
    const [company, setCompany] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => { setError?.(""); }, [setError]);
    useEffect(() => { if (user && user.id) navigate("/", { replace: true }); }, [user, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const ok = await partnerSignup({ email, password, name, company });
        setBusy(false);
        if (ok) navigate("/tracks/brand-foundations", { replace: true });
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-ink-50">
            {/* Editorial side */}
            <div className="hidden lg:flex flex-col justify-between p-12 bg-haevn-700 text-white relative overflow-hidden grain">
                <Logo tone="dark" className="h-7" showSub />
                <div className="max-w-md">
                    <div className="text-[10px] uppercase tracking-[0.25em] opacity-70 font-mono mb-4">Agency Partner Onboarding</div>
                    <h1 className="font-editorial text-4xl xl:text-5xl leading-[1.05] mb-6">
                        Know the brand<br />
                        <span className="opacity-70">before you represent it.</span>
                    </h1>
                    <p className="text-sm text-white/70 leading-relaxed">
                        Brand Foundations is open to every HAEVN agency partner. Learn what we stand for,
                        how we sound, and how to position HAEVN in the market.
                    </p>
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-50 font-mono">Brand Foundations · Agency Partner Access</div>
            </div>

            {/* Form side */}
            <div className="flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-sm" data-testid="partner-signup-card">
                    <div className="lg:hidden mb-10">
                        <Logo className="h-7" showSub />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Agency Partner Sign Up</div>
                    <h2 className="font-editorial text-3xl text-ink-900 mb-1">Create your access.</h2>
                    <p className="text-sm text-ink-500 mb-8">Free for HAEVN agency partners. Takes under a minute.</p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="partner-signup-form">
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Your name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                data-testid="partner-signup-name-input"
                                className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700 transition-colors"
                                placeholder="Jane Cortez"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Agency name</label>
                            <input
                                type="text"
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                data-testid="partner-signup-company-input"
                                className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700 transition-colors"
                                placeholder="e.g. Argyle Creative"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Work email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="partner-signup-email-input"
                                className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700 transition-colors"
                                placeholder="you@youragency.com"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                data-testid="partner-signup-password-input"
                                className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700 transition-colors"
                                placeholder="At least 6 characters"
                            />
                        </div>
                        {error && (
                            <div className="text-xs text-clay-500 bg-clay-50 border border-clay-50 px-3 py-2 rounded-sm" data-testid="partner-signup-error">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="partner-signup-submit-btn"
                            className="w-full bg-haevn-700 text-white py-2.5 rounded-sm hover:bg-haevn-900 transition-colors font-medium text-sm disabled:opacity-50"
                        >
                            {busy ? "Creating account…" : "Create partner account"}
                        </button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-ink-200 text-center">
                        <span className="text-sm text-ink-500">Already have an account? </span>
                        <Link to="/login" data-testid="partner-signup-to-login-link" className="text-sm text-haevn-700 hover:text-haevn-900 underline underline-offset-4 font-medium">Sign in</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
